import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight, Crown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  SKIN_ROUTINES,
  DEFAULT_SKIN,
} from "../data/routines";
import { BEAUTY_TIPS, pickDaily } from "../data/affirmations";
import { supabase } from "../lib/supabase";
import { openCheckout } from "../lib/paddle";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

// Match articles to user's skin/hair profile (very simple keyword overlap).
function recommendedArticles(articles, profile, n = 2) {
  const skin = (profile?.skin_type || "").toLowerCase();
  const hair = (profile?.hair_type || "").toLowerCase();
  const concerns = (profile?.concerns || [])
    .map((c) => c.replace(/^(skin|hair):/i, ""))
    .join(" ")
    .toLowerCase();

  const scored = articles.map((a) => {
    let score = 0;
    const hay = `${a.title} ${a.excerpt}`.toLowerCase();
    if (concerns.includes("hyperpigment") && hay.includes("hyperpigment")) score += 5;
    if (concerns.includes("dark spot") && hay.includes("pigment")) score += 4;
    if (skin.includes("acne") && hay.includes("acne")) score += 4;
    if (skin.includes("dry") && hay.includes("hydra")) score += 3;
    if (skin.includes("oily") && hay.includes("oil")) score += 3;
    if (skin.includes("sensitive") && hay.includes("barrier")) score += 3;
    if (hair.includes("4c") && (hay.includes("4c") || hay.includes("loc"))) score += 5;
    if (hair.includes("coily") && hay.includes("coily")) score += 4;
    if (hair.includes("loc") && hay.includes("loc")) score += 4;
    if (hair.includes("curl") && hay.includes("curl")) score += 3;
    if (hay.includes("spf") || hay.includes("skincare")) score += 1;
    return { a, score };
  });
  scored.sort((x, y) => y.score - x.score);
  const out = [];
  for (const { a } of scored) {
    if (out.length >= n) break;
    if (out.length === 0 && a.category === "Skincare") out.push(a);
    else if (out.length === 1 && a.category === "Haircare") out.push(a);
  }
  for (const { a } of scored) {
    if (out.length >= n) break;
    if (!out.find((x) => x.id === a.id)) out.push(a);
  }
  return out;
}

const ARTICLE_THUMB = {
  Skincare: { bg: "#E8C8A8", emoji: "🌿" },
  Haircare: { bg: "#C8A580", emoji: "💧" },
  Ingredients: { bg: "#FDF5E8", emoji: "🧪" },
  Wellness: { bg: "#E8FDF0", emoji: "🌸" },
  "DIY Tips": { bg: "#FDE8F0", emoji: "🥥" },
};

export default function Dashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const skinKey = profile?.skin_type || DEFAULT_SKIN;
  const skinRoutine = SKIN_ROUTINES[skinKey] || SKIN_ROUTINES[DEFAULT_SKIN];

  const beautyTip = useMemo(() => pickDaily(BEAUTY_TIPS), []);
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    supabase
      .from("articles")
      .select("id, title, category, excerpt, read_time")
      .order("position", { ascending: true })
      .then(({ data }) => setArticles(data || []));
  }, []);
  const recs = useMemo(
    () => recommendedArticles(articles, profile, 2),
    [articles, profile]
  );

  const amSteps = skinRoutine.AM || [];
  const pmSteps = skinRoutine.PM || [];

  // Trial-ending state — banner appears when the user is in a trial that
  // ends within the next 48 hours and they haven't already scheduled a cancel.
  const trialEnd = profile?.trial_end_at ? new Date(profile.trial_end_at) : null;
  const inTrial = trialEnd && trialEnd.getTime() > Date.now();
  const trialEndingSoon = inTrial
    && trialEnd.getTime() - Date.now() < 48 * 3600 * 1000
    && !profile?.cancel_at_period_end;
  const trialDaysLeft = inTrial
    ? Math.max(1, Math.ceil((trialEnd.getTime() - Date.now()) / 86400000))
    : 0;

  // "Start 7-Day Free Trial" banner — show only for users who are NOT premium
  // and NOT already in an active trial. Opens Paddle overlay directly.
  const canStartTrial = !profile?.is_premium && !inTrial;
  const [startingTrial, setStartingTrial] = useState(false);
  const handleStartTrial = async () => {
    if (startingTrial) return;
    setStartingTrial(true);
    try {
      await openCheckout("monthly");
    } catch (e) {
      // Fall back to the Premium page if overlay fails (e.g. Paddle blocked).
      navigate("/app/premium");
    } finally {
      setStartingTrial(false);
    }
  };

  return (
    <div className="-mt-px" data-testid="dashboard-page">
      {/* PINK GRADIENT HEADER */}
      <div
        className="relative pt-5 px-5 pb-6"
        style={{ background: "linear-gradient(180deg, #C9748A 0%, #C9748A 78%, rgba(201,116,138,0.92) 100%)" }}
        data-testid="dashboard-header"
      >
        <div className="flex items-center justify-between mb-6">
          <Link to="/app" data-testid="dashboard-logo">
            <img
              src="/glow-grow-logo.png"
              alt="Glow & Grow"
              className="object-contain"
              style={{ height: 40, width: "auto" }}
            />
          </Link>
          <button
            onClick={() => navigate("/app/profile")}
            data-testid="profile-avatar-btn"
            className="w-10 h-10 rounded-full bg-white/25 backdrop-blur-sm text-white flex items-center justify-center font-medium uppercase text-base"
          >
            {profile?.name?.[0] || "U"}
          </button>
        </div>
        <p className="text-xs text-white/80 font-light tracking-wide">
          {greeting()},
        </p>
        <h1 className="font-serif text-3xl text-white leading-tight">
          {profile?.name || "Beautiful"}{" "}
          <span className="text-2xl">✨</span>
        </h1>
      </div>

      {/* MAIN CONTENT */}
      <div className="px-5 pt-5 pb-24 space-y-5">
        {/* Start 7-Day Free Trial banner — only for non-premium users with no active trial */}
        {canStartTrial && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full rounded-2xl p-4 flex items-center gap-3 shadow-floating"
            style={{ background: "linear-gradient(135deg, #C9748A 0%, #B8657A 100%)" }}
            data-testid="start-trial-banner"
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}>
              <Crown size={18} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium leading-tight text-white">
                Unlock Glow Premium
              </p>
              <p className="text-[11px] font-light text-white/85 mt-0.5">
                7 days free, then $12/mo. Cancel anytime.
              </p>
            </div>
            <button
              type="button"
              onClick={handleStartTrial}
              disabled={startingTrial}
              data-testid="start-trial-btn"
              className="rounded-full bg-white text-cocoa text-xs font-medium px-4 py-2 shrink-0 disabled:opacity-60"
            >
              {startingTrial ? "Opening…" : "Start Free Trial"}
            </button>
          </motion.div>
        )}

        {/* Trial ending soon banner — shows only when trial ends in < 48h */}
        {trialEndingSoon && (
          <motion.button
            type="button"
            onClick={() => navigate("/app/premium")}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full rounded-2xl p-4 flex items-center gap-3 text-left"
            style={{ background: "linear-gradient(135deg, #FDECEF 0%, #F8E1D5 100%)", border: "1px solid #C9748A" }}
            data-testid="trial-ending-banner"
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-lg shrink-0"
                 style={{ background: "linear-gradient(135deg, #C9748A 0%, #B85E75 100%)" }}>⏰</div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium leading-tight" style={{ color: "#2E1608" }}>
                Your free trial ends in {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"}.
              </p>
              <p className="text-[11px] font-light text-cocoa/65 mt-0.5">
                You&apos;ll be charged automatically. Tap to manage or cancel.
              </p>
            </div>
            <ChevronRight size={16} className="text-cocoa/40 shrink-0" />
          </motion.button>
        )}

        {/* Today's Beauty Tip — gold left border */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-5 border border-[#f0e8e4]"
          style={{ borderLeft: "4px solid #C9975A" }}
          data-testid="beauty-tip-card"
        >
          <p className="text-[10px] uppercase tracking-[0.22em] font-medium mb-2" style={{ color: "#C9975A" }}>
            Today's beauty tip
          </p>
          <p className="text-[15px] font-light leading-relaxed" style={{ color: "#2E1608" }}>
            {beautyTip}
          </p>
        </motion.div>

        {/* Skincare Routine preview */}
        <div
          className="bg-white rounded-2xl p-5 border border-[#f0e8e4]"
          data-testid="routine-preview-card"
        >
          <p className="text-[10px] uppercase tracking-[0.22em] font-medium text-rose mb-4">
            Your skincare routine
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <RoutineColumn
              label="Morning"
              icon="☀️"
              steps={amSteps}
              onTap={() => navigate("/app/routines?time=AM")}
            />
            <RoutineColumn
              label="Evening"
              icon="🌙"
              steps={pmSteps}
              onTap={() => navigate("/app/routines?time=PM")}
            />
          </div>
          <button
            onClick={() => navigate("/app/routines?time=AM")}
            data-testid="start-morning-btn"
            className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3 font-medium text-sm transition-all shadow-rose flex items-center justify-center gap-2"
          >
            Start Morning Routine
            <ArrowRight size={16} />
          </button>
        </div>

        {/* The Beauty Edit (Quick Access) */}
        <div data-testid="beauty-edit-section">
          <h2 className="font-serif text-2xl mb-0.5" style={{ color: "#2E1608" }}>
            The Beauty Edit
          </h2>
          <p className="text-xs font-light text-cocoa/55 mb-4">
            Your curated beauty tools
          </p>
          <div className="grid grid-cols-2 gap-3">
            <ColorTile
              to="/app/diy"
              testId="tile-diy"
              bg="#E8FDF0"
              titleColor="#1A5C35"
              title="DIY Zone"
              subtitle="Beauty recipes from your kitchen"
              illustration={<DIYIllustration />}
            />
            <ColorTile
              to="/app/insights"
              testId="tile-insights"
              bg="#E8F0FD"
              titleColor="#1A3D8B"
              title="Your Glow Report"
              subtitle="Daily skin & hair intel"
              illustration={<InsightsIllustration />}
            />
            <ColorTile
              to="/app/progress"
              testId="tile-progress"
              bg="#FDE8F0"
              titleColor="#8B2252"
              title="Progress"
              subtitle="Track your glow journey"
              illustration={<ProgressIllustration />}
            />
            <ColorTile
              to="/app/library"
              testId="tile-library"
              bg="#FDF5E8"
              titleColor="#7A4A0A"
              title="Library"
              subtitle="Expert beauty articles"
              illustration={<BookIllustration />}
            />
          </div>
        </div>

        {/* Recommended for You */}
        <div data-testid="recommended-section" className="pt-1">
          <h2 className="font-serif text-2xl mb-0.5" style={{ color: "#2E1608" }}>
            Recommended for You
          </h2>
          <p className="text-xs font-light text-cocoa/55 mb-4">
            Based on your profile
          </p>
          <div className="space-y-3">
            {recs.map((a) => {
              const thumb = ARTICLE_THUMB[a.category] || ARTICLE_THUMB.Skincare;
              return (
                <Link
                  key={a.id}
                  to={`/app/library`}
                  state={{ openArticle: a.id }}
                  data-testid={`reco-article-${a.id}`}
                  className="block bg-white rounded-2xl p-4 border border-[#f0e8e4]"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl shrink-0"
                      style={{ background: thumb.bg }}
                    >
                      {thumb.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-rose font-medium mb-0.5">
                        {a.category}
                      </p>
                      <h3 className="font-serif text-base leading-tight text-cocoa mb-1">
                        {a.title}
                      </h3>
                      <p className="text-[11px] text-cocoa/55">
                        {a.read_time} read →
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function RoutineColumn({ label, icon, steps, onTap }) {
  const preview = steps.slice(0, 3);
  const remaining = Math.max(0, steps.length - 3);
  return (
    <button
      onClick={onTap}
      className="text-left rounded-xl p-3 bg-white border border-[#f0e8e4]"
      data-testid={`routine-col-${label.toLowerCase()}`}
    >
      <p className="text-xs font-medium mb-2 flex items-center gap-1.5" style={{ color: "#2E1608" }}>
        <span className="text-base">{icon}</span>
        {label} · {steps.length} steps
      </p>
      <div className="space-y-1.5">
        {preview.map((s, i) => (
          <div
            key={i}
            className="bg-white rounded-full text-[10px] text-cocoa/80 px-2.5 py-1 border border-[#f0e8e4] truncate"
          >
            {s}
          </div>
        ))}
        {remaining > 0 && (
          <div className="bg-white rounded-full text-[10px] text-rose font-medium px-2.5 py-1 border border-rose/30 text-center">
            +{remaining} more
          </div>
        )}
      </div>
    </button>
  );
}

function ColorTile({ to, testId, bg, titleColor, title, subtitle, illustration }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="rounded-2xl p-4 min-h-[110px] flex flex-col justify-between transition-transform active:scale-[0.98]"
      style={{ background: bg }}
    >
      <div className="h-8 flex items-start">{illustration}</div>
      <div>
        <p className="font-serif text-base leading-tight font-medium mb-0.5" style={{ color: titleColor }}>
          {title}
        </p>
        <p className="text-[10px] font-light leading-snug" style={{ color: titleColor, opacity: 0.75 }}>
          {subtitle}
        </p>
      </div>
    </Link>
  );
}

/* ===== Inline SVG illustrations with subtle CSS animations ===== */

function DIYIllustration() {
  // Mortar with leaf — animated subtle bounce
  return (
    <svg width="32" height="30" viewBox="0 0 32 32" fill="none" aria-hidden>
      {/* Mortar bowl */}
      <path
        d="M 6 16 Q 6 26 16 27 Q 26 26 26 16 Z"
        fill="#1A5C35"
        opacity="0.85"
      />
      {/* Pestle */}
      <g style={{ transformOrigin: "16px 14px", animation: "diyBounce 1.6s ease-in-out infinite" }}>
        <rect x="14.5" y="3" width="3" height="12" rx="1.4" fill="#1A5C35" />
      </g>
      {/* Leaf */}
      <path
        d="M 21 9 Q 27 7 28 13 Q 24 14 21 9 Z"
        fill="#7FB069"
      />
      <line x1="22" y1="10" x2="26" y2="12" stroke="#1A5C35" strokeWidth="0.6" />
      <style>{`@keyframes diyBounce { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-2px) } }`}</style>
    </svg>
  );
}

function DupeIllustration() {
  return (
    <svg width="30" height="30" viewBox="0 0 32 32" fill="none" aria-hidden>
      {/* Bottle */}
      <rect x="9" y="9" width="10" height="16" rx="2" fill="#8B2252" opacity="0.85" />
      <rect x="11" y="6" width="6" height="4" rx="1" fill="#8B2252" />
      {/* Magnifying glass — animated */}
      <g style={{ transformOrigin: "22px 22px", animation: "dupeMag 2.4s ease-in-out infinite" }}>
        <circle cx="22" cy="22" r="5" stroke="#8B2252" strokeWidth="2" fill="white" />
        <line x1="26" y1="26" x2="30" y2="30" stroke="#8B2252" strokeWidth="2" strokeLinecap="round" />
      </g>
      <style>{`@keyframes dupeMag { 0%,100% { transform: translate(0,0) } 50% { transform: translate(-3px,-2px) } }`}</style>
    </svg>
  );
}

function InsightsIllustration() {
  return (
    <svg width="30" height="30" viewBox="0 0 32 32" fill="none" aria-hidden>
      <g style={{ animation: "twinkle1 2s ease-in-out infinite" }}>
        <path d="M16 4 L18 12 L26 14 L18 16 L16 24 L14 16 L6 14 L14 12 Z" fill="#1A3D8B" />
      </g>
      <g style={{ animation: "twinkle2 1.6s ease-in-out infinite 0.4s" }}>
        <path d="M26 6 L27 9 L30 10 L27 11 L26 14 L25 11 L22 10 L25 9 Z" fill="#3F5FB8" />
      </g>
      <g style={{ animation: "twinkle2 1.6s ease-in-out infinite 0.9s" }}>
        <path d="M6 22 L7 24 L9 25 L7 26 L6 28 L5 26 L3 25 L5 24 Z" fill="#3F5FB8" />
      </g>
      <style>{`
        @keyframes twinkle1 { 0%,100% { opacity:0.55; transform:scale(0.92) } 50% { opacity:1; transform:scale(1) } }
        @keyframes twinkle2 { 0%,100% { opacity:0.3 } 50% { opacity:1 } }
      `}</style>
    </svg>
  );
}

function ProgressIllustration() {
  return (
    <svg width="34" height="30" viewBox="0 0 36 32" fill="none" aria-hidden>
      <polyline
        points="3,26 11,18 18,22 26,10 33,4"
        stroke="#1A5C35"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        style={{
          strokeDasharray: 60,
          strokeDashoffset: 60,
          animation: "drawline 2s ease-out infinite",
        }}
      />
      <polygon points="33,4 30,4 32,7" fill="#1A5C35" />
      <style>{`@keyframes drawline { 0% { stroke-dashoffset:60 } 60%,100% { stroke-dashoffset:0 } }`}</style>
    </svg>
  );
}

function BookIllustration() {
  return (
    <svg width="34" height="30" viewBox="0 0 36 32" fill="none" aria-hidden>
      <rect x="4" y="6" width="28" height="22" rx="2" fill="#7A4A0A" opacity="0.15" />
      <g style={{ transformOrigin: "18px 17px", animation: "pageflip 2.4s ease-in-out infinite" }}>
        <rect x="6" y="8" width="12" height="18" rx="1" fill="#FDF5E8" stroke="#7A4A0A" strokeWidth="1" />
      </g>
      <rect x="18" y="8" width="12" height="18" rx="1" fill="#FDF5E8" stroke="#7A4A0A" strokeWidth="1" />
      <line x1="18" y1="8" x2="18" y2="26" stroke="#7A4A0A" strokeWidth="1.2" />
      <style>{`@keyframes pageflip { 0%,100% { transform: rotateY(0) } 50% { transform: rotateY(-55deg) } }`}</style>
    </svg>
  );
}
